/**
 * A apresentação que a pessoa vê uma vez, logo depois de entrar.
 *
 * O momento mais frágil de um produto é o primeiro minuto: a conta está
 * vazia, nada foi criado ainda, e um painel em branco não explica nada. Estas
 * quatro telas mostram o caminho inteiro — subir, marcar, publicar — com
 * imagens do produto de verdade, não ilustração genérica.
 *
 * Pode pular a qualquer momento. Prender alguém numa apresentação é a maneira
 * mais rápida de fazer com que ela nunca mais seja lida.
 *
 * Marcada como vista na CONTA, não no aparelho: quem já assistiu no
 * computador não deve assistir de novo ao abrir no celular.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';

interface Step {
  key: 's1' | 's2' | 's3' | 's4';
  image: string;
  /** Onde o olhar deve cair na imagem quando ela for recortada. */
  focus?: string;
}

const STEPS: Step[] = [
  { key: 's1', image: '/landing/img/ai-staging-after.webp', focus: '50% 55%' },
  { key: 's2', image: '/landing/img/how-upload.webp' },
  { key: 's3', image: '/landing/img/how-points.webp' },
  { key: 's4', image: '/landing/img/how-publish.webp' },
];

export function WelcomeTour({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const [i, setI] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const touchX = useRef<number | null>(null);
  const step = STEPS[i];
  const last = i === STEPS.length - 1;

  /** Marca como vista e sai. Nunca falha para o usuário: se a rede cair, o
   *  pior caso é ver a apresentação de novo — melhor do que travar a saída. */
  const finish = useCallback(() => {
    if (leaving) return;
    setLeaving(true);
    void api.request('/auth/onboarding/seen', { method: 'POST' }).catch(() => {});
    onClose();
  }, [leaving, onClose]);

  const go = useCallback((delta: number) => {
    setI((cur) => Math.min(STEPS.length - 1, Math.max(0, cur + delta)));
  }, []);

  // As próximas imagens são buscadas com antecedência: um passo que aparece em
  // branco e só depois carrega quebra o ritmo justamente onde ele importa.
  useEffect(() => {
    STEPS.forEach((s) => {
      const img = new Image();
      img.src = s.image;
    });
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'Escape') finish();
      else if (e.key === 'Enter' && last) finish();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, finish, last]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#08090c] p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={t('onboarding:s1.eyebrow')}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        touchX.current = null;
        // 60px para não confundir um toque trêmulo com um gesto.
        if (Math.abs(dx) > 60) go(dx < 0 ? 1 : -1);
      }}
    >
      <div className="relative w-full max-w-4xl overflow-hidden rounded-2xl border border-white/10 bg-[#0E1016] shadow-2xl">
        <button
          type="button"
          onClick={finish}
          aria-label={t('onboarding:skip')}
          className="absolute right-3 top-3 z-10 flex h-9 items-center gap-1.5 rounded-full bg-black/50 px-3 text-xs font-semibold text-white/70 backdrop-blur-sm transition hover:bg-black/70 hover:text-white"
        >
          {t('onboarding:skip')} <X className="h-3.5 w-3.5" />
        </button>

        <div className="grid md:grid-cols-2">
          {/* A imagem vem primeiro no telefone: é ela que diz do que se trata
              antes de qualquer palavra ser lida. */}
          <div className="relative aspect-[16/10] md:aspect-auto md:min-h-[420px]">
            <AnimatePresence mode="wait">
              <motion.img
                key={step.image}
                src={step.image}
                alt=""
                initial={reduced ? false : { opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={reduced ? undefined : { opacity: 0 }}
                transition={{ duration: 0.45, ease: [0.16, 0.8, 0.24, 1] }}
                className="absolute inset-0 h-full w-full object-cover"
                style={{ objectPosition: step.focus ?? '50% 50%' }}
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0E1016] via-transparent to-transparent md:bg-gradient-to-r" />
          </div>

          <div className="flex flex-col justify-center gap-4 p-6 sm:p-9">
            <AnimatePresence mode="wait">
              <motion.div
                key={step.key}
                initial={reduced ? false : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduced ? undefined : { opacity: 0, y: -8 }}
                transition={{ duration: 0.35, ease: [0.16, 0.8, 0.24, 1] }}
                className="space-y-3"
              >
                <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--color-accent)]">
                  {t(`onboarding:${step.key}.eyebrow`)}
                </span>
                <h2 className="text-2xl font-bold leading-tight text-white sm:text-[28px]" style={{ textWrap: 'balance' }}>
                  {t(`onboarding:${step.key}.title`)}
                </h2>
                <p className="text-sm leading-relaxed text-white/65 sm:text-[15px]">
                  {t(`onboarding:${step.key}.text`)}
                </p>
              </motion.div>
            </AnimatePresence>

            <div className="mt-2 flex items-center gap-3">
              {/* Pontos clicáveis: quem quer rever um passo não deveria ter que
                  atravessar os outros de novo. */}
              <div className="flex gap-1.5" role="tablist">
                {STEPS.map((s, idx) => (
                  <button
                    key={s.key}
                    type="button"
                    role="tab"
                    aria-selected={idx === i}
                    aria-label={t('onboarding:step', { current: idx + 1, total: STEPS.length })}
                    onClick={() => setI(idx)}
                    className={`h-1.5 rounded-full transition-all ${
                      idx === i ? 'w-6 bg-[var(--color-primary)]' : 'w-1.5 bg-white/25 hover:bg-white/40'
                    }`}
                  />
                ))}
              </div>
              <span className="font-mono text-[11px] text-white/35">
                {t('onboarding:step', { current: i + 1, total: STEPS.length })}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {i > 0 && (
                <Button variant="outline" size="sm" onClick={() => go(-1)} className="border-white/15 text-white/80">
                  <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
                  {t('onboarding:back')}
                </Button>
              )}
              <Button variant="primary" onClick={() => (last ? finish() : go(1))} className="flex-1 sm:flex-none">
                {last ? t('onboarding:done') : t('onboarding:next')}
                {!last && <ArrowRight className="ml-1.5 h-3.5 w-3.5" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Decide sozinho se deve aparecer, perguntando ao servidor.
 *
 * Montado uma vez na raiz: espalhar essa decisão por várias telas é como ela
 * acaba aparecendo duas vezes, ou nunca.
 */
export function WelcomeTourMount({ enabled }: { enabled: boolean }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    void api
      .request<{ success: boolean; data: { seen: boolean } }>('/auth/onboarding')
      .then((r) => {
        if (alive && r.data && !r.data.seen) setShow(true);
      })
      .catch(() => {
        // Sem resposta, não mostra: aparecer por engano para quem já viu é
        // pior do que não aparecer para quem ainda não viu.
      });
    return () => { alive = false; };
  }, [enabled]);

  if (!show) return null;
  return <WelcomeTour onClose={() => setShow(false)} />;
}

export default WelcomeTour;
